import { PubSub } from '@aws-amplify/pubsub';
import {
	AddIcCallOutlined,
	CloseOutlined,
	DeleteForeverOutlined,
	DoneOutline,
	PersonOutline,
	PhoneOutlined,
	RestoreOutlined,
	WarningAmberOutlined
} from '@mui/icons-material';
import {
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Fade,
	FormControl,
	IconButton,
	InputLabel,
	List,
	ListItem,
	MenuItem,
	Select,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography
} from '@mui/material';
import { generateClient, get, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { enqueueSnackbar } from 'notistack';
import phone from 'phone';
import { useContext, useEffect, useRef, useState } from 'react';
import { ActivityLog } from 'src/components/contacts/activityLog';
import { CallStatusPane } from 'src/components/engagement/callStatusPane';
import { campaignsByTenant, getContact, getSegment } from 'src/graphql/queries';
import { REGION } from 'src/models/globals';
import {
	actionTwoButtonStyle,
	tabStyle
} from 'src/theme';
import { UserContext } from '../../contexts/UserContext';
import { Cache } from 'aws-amplify/utils';

export function PreviewDialing(props) {
	const client = generateClient();

	/** The user context state */
	const userContext = useContext(UserContext);

	/** Whether something is loading */
	const [loading, setLoading] = useState(true);

	/** Campaigns agent can preview dial from */
	const [allCampaigns, setAllCampaigns] = useState([]);

	/** Which Campaign Agent is dialing for */
	const selectedCampaignRef = useRef(null);
	const [selectedCampaign, setSelectedCampaign] = useState(null);

	const [hasRehydratedSelectedCampaign, setHasRehydratedSelectedCampaign] = useState(false);

	/** Allow multiple dials for a preview contact */
	const [allowMultipleDials, setAllowMultipleDials] = useState(false);
	const [calledFields, setCalledFields] = useState([]);
	const [allowManualNumberEntry, setAllowManualNumberEntry] = useState(false);
	const [openManualNumberDialog, setOpenManualNumberDialog] = useState(false);
	const [manualNumber, setManualNumber] = useState('');

	const [retrievingContact, setRetrievingContact] = useState(false);

	/** The full active contact data being preview dialed */
	const [activeContact, setActiveContact] = useState(null);

	/** The hash of the active contact */
	const contactHash = useRef(null);

	const [callingContact, setCallingContact] = useState(false);
	const [callingContactField, setCallingContactField] = useState('');

	const [callStatus, setCallStatus] = useState('');

	const [phoneFields, setPhoneFields] = useState([]);

	const [currentTab, setCurrentTab] = useState(0);

	const [suppressionProfileId, setSuppressionProfileId] = useState('');

	const pubsubRef = useRef(
		new PubSub({
			region: REGION,
			endpoint: `wss://a3b8mw0zsvy66t-ats.iot.${REGION}.amazonaws.com/mqtt`
		}),
	);

	/** Subscription for the go-ahead on allowing the agent to dial a number. Needed for Webex. */
	const agentAllowDialSubscriptionRef = useRef(null);
	const callStatusSubscription = useRef(null);

	/**
	 * Sets up listeners and subscriptions.
	 */
	useEffect(() => {
		subscribeToAllowDial();
		if (userContext.telephonyProvider === 'Webex') {
			addWebexCallEventListeners();
		}

		// Clean up subscriptions and listeners. Without this, there is a memory leak.
		return () => {
			unsubscribeFromAll();
			if (userContext.telephonyProvider === 'Webex') {
				removeWebexCallEventListeners();
			}
		};
	}, [userContext]);

	useEffect(() => {
		if (userContext.apiKey) {
			getCampaigns();
		}
		const tempPhoneFields = [
			'phone',
			'cell',
			...userContext.customFields
				.filter((field) => field.type === 'Phone')
				.map((field) => field.name),
		];
		setPhoneFields(tempPhoneFields);
	}, [userContext.apiKey]);

	useEffect(() => {
		setLoading(false);
		if (allCampaigns.length > 0 && !hasRehydratedSelectedCampaign) {
			rehydrateSelectedCampaign();
		}
	}, [allCampaigns]);

	useEffect(() => {
		if (selectedCampaign && !activeContact && !contactHash.current) {
			getContactForDialing();
		}
	}, [selectedCampaign, activeContact]);

	useEffect(() => {
		selectedCampaignRef.current = selectedCampaign;
		// Ignore the initial state.
		if (hasRehydratedSelectedCampaign) {
			Cache.setItem('previewDialingCampaign', selectedCampaign);
		}
		subscribeToCallStatus();
		const previewConfig = JSON.parse(
			selectedCampaign?.profile?.previewConfig || '{}',
		);
		setAllowMultipleDials(previewConfig?.previewAllowMultiDial || false);
		setAllowManualNumberEntry(previewConfig?.allowManualDial || false);

		// If an active contact exists we need to recyle it since the campaign has changed.
		if (activeContact) {
			handleContactAction(contactHash.current.phone, 'recycle');
			setActiveContact(null);
			setCalledFields([]);
			setCallingContactField('');
			setCallStatus('');
			contactHash.current = null;
		}
	}, [selectedCampaign]);

	/**
	 * Re-hydrates the selected campaign from the cache.
	 */
	async function rehydrateSelectedCampaign() {
		const cachedSelectedCampaign = await Cache.getItem('previewDialingCampaign');
		if (cachedSelectedCampaign) {
			// Remove the cached campaign, if it is no longer valid.
			if (!allCampaigns.find((campaign) => campaign.id === cachedSelectedCampaign.id)) {
				await Cache.removeItem('previewDialingCampaign');
			} else {
				setSelectedCampaign(cachedSelectedCampaign);
			}
		}
		setHasRehydratedSelectedCampaign(true);
	}

	/**
	 * Subscribes to the allow dial message for Webex.
	 */
	async function subscribeToAllowDial() {
		agentAllowDialSubscriptionRef.current?.unsubscribe();
		const baseTopic = `approvals/${process.env.REACT_APP_ENV}/${userContext.tenantId}`;
		agentAllowDialSubscriptionRef.current = pubsubRef.current
			.subscribe({
				topics: `${baseTopic}/c2c/${props.agentId}`,
			})
			.subscribe({
				next: (message) => handleAllowDialMessage(message),
				error: (error) => {
					console.error(`Error in agent allow dial subscription: ${error}`);
					handleThirdErrorReceived();
				},
				complete: () => console.log('Allow dial watcher closed.'),
			});
	}

	async function subscribeToCallStatus() {
		try {
			callStatusSubscription.current?.unsubscribe();
			callStatusSubscription.current = pubsubRef.current
				.subscribe({
					topics: `approvals/${process.env.REACT_APP_ENV}/${userContext.tenantId}/agent-call-statuses/${props.agentId}`,
				})
				.subscribe({
					next: (message) => {
						handleCallStatusChangeMessage(message);
					},
					error: (error) => {
						console.error('Error subscribing to call status', error);
					},
					complete: () => {
						console.log('Call Status subscription complete');
					},
				});
		} catch (error) {
			console.error('Error subscribing to call status', error);
		}
	}

	/**
	 * Unsubscribes from all subscriptions.
	 */
	function unsubscribeFromAll() {
		agentAllowDialSubscriptionRef.current?.unsubscribe();
		callStatusSubscription.current?.unsubscribe();
	}

	/**
	 * Updates the call status on the approved contact when the status changes.
	 *
	 * @param {*} message The message received from the WebSocket.
	 */
	async function handleCallStatusChangeMessage(message) {
		console.log('Call status change message received', message);
		setCallStatus(message.callStatus);
	}

	/**
	 * Starts the dial or displays an error message when the agent is not allowed to dial.
	 *
	 * @param {*} message The message received from the WebSocket.
	 */
	async function handleAllowDialMessage(message) {
		console.log('Allow dial message received', message);
		const approvedContact = contactHash.current;
		if (approvedContact === undefined) {
			// Cannot catch this.
			console.error('Approved contact not found');
			return;
		}

		try {
			// If the agent is not allowed to dial, just update the call status to 'Not Allowed'.
			if (!message.allowDial) {
				setCallStatus('Not Allowed To Dial');
				return;
			}

			// Start the dial, if using Webex CC.
			if (userContext.telephonyProvider === 'Webex') {
				const { Desktop } = await import('@wxcc-desktop/sdk');
				const campaignCciMetadata = JSON.parse(selectedCampaignRef.current.cciMetaData);
				const outDial = await Desktop.dialer.startOutdial({
					data: {
						entryPointId: selectedCampaignRef.current.skillId,
						destination: approvedContact.phone,
						direction: 'OUTBOUND',
						origin: campaignCciMetadata.wbxOutDialAni,
						attributes: {
							FirstName: approvedContact.firstName,
							LastName: approvedContact.lastName,
							contactId: approvedContact.id,
							campaignId: approvedContact.campaignId,
							segmentId: approvedContact.segmentId,
							externalId: approvedContact.externalId,
						},
						mediaType: 'telephony',
						outboundType: 'OUTDIAL',
					},
				});
				console.debug('Outdial started', outDial);
				setCallStatus('Dialing...');
			}
		} catch (error) {
			console.error('Error', error);
			setCallStatus('Error');
		}
	}

	async function getCampaigns() {
		try {
			setLoading(true);
			const idToken = (await fetchAuthSession()).tokens.idToken;

			const campaignsResponse = await client.graphql({
				query: campaignsByTenant,
				variables: {
					tenant: idToken.payload['custom:tenant'],
					filter: { status: { eq: 'Active' } },
					limit: 1000,
				},
			});

			const returnedCampaigns =
				campaignsResponse.data.campaignsByTenant?.items ?? [];

			// Filter the campaigns based on whether they are Click2Call-enabled or not.
			let filteredCampaigns = returnedCampaigns.filter((campaign) => campaign.profile?.previewDialing);

			if (userContext.telephonyProvider === 'NiC') {
				// Further filter the campaigns for the agent's available skills.
				filteredCampaigns = filteredCampaigns.filter((campaign) => props.agentSkillIds.includes(campaign.skillId));
			} else if (userContext.telephonyProvider === 'Webex') {
				// Further filter the campaigns for the agent's selected team.
				const campaignIdsResponse = await get({
					apiName: 'cdyxoutreach',
					path: '/cci/webex/contact-center/agent-campaigns/' + props.teamId,
					options: {
						headers: {
							Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
							'x-api-key': userContext.apiKey,
						},
					},
				}).response;
				campaignIdsForTeam = (await campaignIdsResponse.body.json()) ?? [];

				filteredCampaigns = filteredCampaigns.filter((campaign) =>
					campaignIdsForTeam.includes(campaign.id),
				);
			}
			// FIXME: We should notify the user if there are no campaigns available.
			setAllCampaigns(filteredCampaigns);
		} catch (error) {
			console.error(error);
			// FIXME: We should probably notify the user that there was an error.
		}
	}

	async function getContactForDialing() {
		try {
			setRetrievingContact(true);
			const contactRequest = await get({
				apiName: 'cdyxoutreach',
				path: `/contacts/${selectedCampaign.skillId}`,
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					queryParams: {
						total: 1,
					},
				},
			}).response;

			try {
				const contactHashResponse = await contactRequest.body.json();
				console.log(contactHashResponse);
				contactHash.current = contactHashResponse[0];
				getSuppressionProfileId(contactHashResponse[0].segmentId);
				let fullContact = await getDynamoContact(contactHashResponse[0].id);
				console.log('Full contact retrieved', fullContact);
				fullContact.customFields = fullContact.customFields
					? JSON.parse(fullContact.customFields)
					: null;
				setActiveContact(fullContact);
			} catch (err) {
				// FIXME: We should
				console.error('Error parsing contact response', err);
				// FIXME: We should probably notify the user that there are no contacts available.
				console.log('No contact available for dialing');
			}
		} catch (error) {
			console.error(error);
			// FIXME: We should probably notify the user that there was an error.
		}
		setRetrievingContact(false);
	}

	async function getSuppressionProfileId(segmentId) {
		try {
			const segmentRequest = await client.graphql({
				query: getSegment,
				variables: {
					id: segmentId,
				},
			});

			const segmentData = segmentRequest.data.getSegment;
			if (segmentData) {
				setSuppressionProfileId(segmentData.suppressionID);
			}

		} catch (error) {
			console.error('Error getting segment', error);
		}
	}

	async function getDynamoContact(contactId) {
		// Don't swallow any errors here. We want them to bubble up.
		const contactRequest = await client.graphql({
			query: getContact,
			variables: {
				id: contactId,
			},
		});
		return contactRequest.data.getContact;
	}

	async function handleContactAction(phoneNumber, type) {
		try {

			// Check if we can dial.
			setCallingContact(true);
			if (type === 'approve') {
				enqueueSnackbar('Checking contact is valid for dialing', {
					variant: 'info',
					autoHideDuration: 2000,
				});
				const checkResult = await post({
					apiName: 'cdyxoutreach',
					path: '/dial/check',
					options: {
						headers: {
							Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
							'x-api-key': userContext.apiKey,
						},
						body: {
							contact: { ...activeContact, phone: phoneNumber },
							field: 'phone',
							profileId: selectedCampaign.campaignProfileId,
							tenantId: userContext.tenantId,
							suppressionProfileId: suppressionProfileId,
						}
					}
				}).response;

				const checkResponse = await checkResult.body.json();
				if (checkResponse.Message !== 'Contact passed check') {
					enqueueSnackbar(checkResponse.Message || 'Error checking contact', {
						variant: 'error',
						autoHideDuration: 3000,
					});
					setCallingContact(false);
					return;
				} else {
					enqueueSnackbar(checkResponse.Message + '. Calling...', {
						variant: 'success',
						autoHideDuration: 2000,
					});
				}
			}

			// Dial the contact
			contactHash.current.phone = phoneNumber;
			await pubsubRef.current.publish({
				topics: [
					`approvals/${process.env.REACT_APP_ENV}/${userContext.tenantId}/${selectedCampaign.id}`,
				],
				message: {
					type,
					skillId: selectedCampaign.skillId,
					agent: props.agentId,
					cci: selectedCampaign.cci,
					...contactHash.current,
					phone: phoneNumber,
					jwtToken: (await fetchAuthSession()).tokens.idToken,
					apiKey: userContext.apiKey,
					preview: true,
				},
			});

			if (type !== 'approve') {
				setActiveContact(null);
				setCallingContact(false);
				setCallStatus('');
				setCalledFields([]);
				setCallingContactField('');
				contactHash.current = null;
			}
		} catch (error) {
			console.error('Error calling contact', error);
		}
	}

	async function closePreview() {
		if (activeContact) {
			await handleContactAction(contactHash.current.phone, 'recycle');
			setActiveContact(null);
			setCalledFields([]);
			setCallingContact(false);
			setCallingContactField('');
			setCallStatus('');
			contactHash.current = null;
		}
		callStatusSubscription.current.unsubscribe();
		setSelectedCampaign(null);
		setCallingContact(false);
		setCallingContactField('');
		setCallStatus('');
		contactHash.current = null;
		props.setDialMode('');
	}

	function getPhoneLabel(field) {
		switch (field) {
			case 'phone':
				return 'Phone';
			case 'cell':
				return 'Cell';
			default:
				let label = field;
				userContext.customFields.find((customField) => {
					if (customField.name === field) {
						label = customField.displayName;
					}
				});
				return label;
		}
	}

	/**
	 * Registers listeners for Webex call events to get call progression. This should only be called if the user is using Webex
	 * as the telephony provider.
	 */
	async function addWebexCallEventListeners() {
		try {
			const { Desktop } = await import('@wxcc-desktop/sdk');
			Desktop.agentContact.addEventListener('eAgentContactAssigned', async (event) => {
				await handleCallStatusChangeMessage({ callStatus: 'On Call', contactId: event.data.interaction.callAssociatedData.contactId.value });
				setLoading(false);
			});
			Desktop.agentContact.addEventListener('eAgentContactEnded', async (event) => {
				await handleCallStatusChangeMessage({ callStatus: 'Call Ended - Awaiting Wrap Up', contactId: event.data.interaction.callAssociatedData.contactId.value });
				setLoading(false);
			});
			Desktop.agentContact.addEventListener('eAgentContactWrappedUp', async () => {
				setCallingContact(false);
				setCallingContactField('');
				contactHash.current = null;
				setLoading(false);
			});
		} catch (error) {
			console.error('Error setting up Webex agent contact listeners', error);
		}
	}

	/**
	 * Removes the Webex call event listeners. This should only be called if the user is using Webex as the telephony provider.
	 */
	async function removeWebexCallEventListeners() {
		try {
			const { Desktop } = await import('@wxcc-desktop/sdk');
			Desktop.agentContact.removeAllEventListeners();
		} catch (error) {
			console.error('Error removing Webex agent contact listeners', error);
		}
	}

	return (
		<>
			{/* Preview Dialing */}
			<Box
				sx={{ width: '100%' }}
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="space-around"
				gap={2}
			>
				<Box
					sx={{ maxWidth: '1200px', width: '100%' }}
					display="flex"
					flexDirection="column"
					alignItems="center"
					gap={2}
				>
					{/* Campaign Control */}

					<Box
						sx={{ width: '100%', padding: '10px' }}
						display="grid"
						gridTemplateColumns="5fr 1fr"
						gap={2}
						alignItems="center"
					>
						<FormControl>
							<InputLabel>Dialing Campaign</InputLabel>
							<Select
								inputProps={{ id: 'campaigns' }}
								label="Dialing Campaign"
								value={selectedCampaign ? selectedCampaign.id : ''}
								onChange={(event) => {
									const campaignId = event.target.value;
									const selectedCampaign = allCampaigns.find(campaign => campaign.id === campaignId);
									setSelectedCampaign(selectedCampaign);
									document.activeElement.blur();
								}}
								disabled={allCampaigns.length === 0}
							>
								{allCampaigns.map((campaign) => (
									<MenuItem key={campaign.id} value={campaign.id}>
										{campaign.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{!callingContact && (
							<Box display="flex" gap={2}>
								<Button onClick={closePreview} sx={actionTwoButtonStyle}>
									Close
								</Button>
								{allowMultipleDials &&
									userContext.telephonyProvider === 'Nic' && (
										<IconButton
											sx={
												-[
													{ backgroundColor: '#1559b3' },
													{ '&:hover': { backgroundColor: '#0c3e9a' } },
												]
											}
										>
											<WarningAmberOutlined fontSize="small" color="white" />
										</IconButton>
									)}
							</Box>
						)}
					</Box>

					{/* Contact Control */}

					<Box
						sx={{ width: '100%', padding: '10px' }}
						display="flex"
						flexDirection="column"
						alignItems="center"
					>
						<Card
							sx={{ width: '100%', borderRadius: '7px' }}
							variant="outlined"
						>
							<CardMedia sx={{ height: '200px', backgroundColor: '#b9cde8' }}>
								<Box height="100%">
									{!selectedCampaign && (
										<Fade in={!selectedCampaign}>
											<Box
												display="flex"
												flexDirection="column"
												alignItems="center"
												justifyContent="center"
												sx={{ height: '100%' }}
											>
												<Typography variant="h5">
													Select a campaign to begin dialing
												</Typography>
											</Box>
										</Fade>
									)}
									{retrievingContact && (
										<Fade in={retrievingContact}>
											<Box
												display="flex"
												flexDirection="column"
												alignItems="center"
												justifyContent="center"
												sx={{ height: '100%' }}
											>
												<Typography variant="h5">Getting Contact...</Typography>
											</Box>
										</Fade>
									)}
									{callingContact && (
										<CallStatusPane
											contact={{callStatus}}
											onDismiss={() => {
												setCallingContact(false);
												setCallStatus('');
												if (!allowMultipleDials) {
													setActiveContact(null);
													setCalledFields([]);
													setCallingContactField('');
													contactHash.current = null;
												}
												setCallingContactField('');
											}}
										/>
									)}
								</Box>
							</CardMedia>
							<Box
								sx={{ padding: '10px' }}
								display="grid"
								gridTemplateColumns="1fr 1fr 1fr"
							>
								<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
									{/* <List> */}
									{/* Display the phone field if it exists on the contact */}
									{phoneFields.map((field) => {
										if (
											activeContact &&
											(activeContact[field] ||
												activeContact.customFields?.[field])
										) {
											return (
												<ListItem
													key={field}
													secondaryAction={<>
														{(!callingContact && !calledFields.includes(field)) && (
															<IconButton
																disabled={
																	calledFields.includes(field)
																}
																sx={[
																	{ backgroundColor: '#1559b3' },
																	{ '&:hover': { backgroundColor: '#0c3e9a' } },
																]}
																edge="end"
																onClick={() => {
																	setCallingContactField(field);
																	setCalledFields([
																		...calledFields,
																		field,
																	]);
																	handleContactAction(
																		activeContact[field]
																			? activeContact[field]
																			: activeContact.customFields?.[field],
																		'approve',
																	);
																}}
															>
																<PhoneOutlined fontSize="small" color="white" />
															</IconButton>
														)}
														{!callingContact && calledFields.includes(field) && (
															<DoneOutline fontSize="small" color="primary" />
														)}
														{callingContact &&
															callingContactField === field && (
																<CircularProgress
																	size={24}
																	sx={{ color: '#1559b3' }}
																/>
															)}
													</>

													}
												>
													<Box display="flex" flexDirection="column">
														<Typography variant="body1">
															{activeContact[field]
																? activeContact[field]
																: activeContact.customFields?.[field]}
														</Typography>
														<Typography variant="body2">
															{getPhoneLabel(field)}
														</Typography>
													</Box>
												</ListItem>
											);
										}
									})}
									{/* </List> */}
								</Box>
								<Box
									display="flex"
									flexDirection="column"
									alignItems="center"
									justifyContent="center"
								>
									<Typography variant="h5">
										{activeContact?.firstName} {activeContact?.lastName}
									</Typography>
									{activeContact?.externalId && (
										<Typography align='center' variant="h6">{`External ID: ${activeContact.externalId}`}</Typography>
									)}
								</Box>
								<Box
									display="flex"
									flexDirection="column"
									alignItems="center"
									justifyContent="center"
								>
									{activeContact && !callingContact && (
										<Box
											display="flex"
											flexDirection="row"
											gap={2}
											justifyContent="center"
											alignItems="center"
										>
											{allowManualNumberEntry && (
												<Tooltip title="Dial a manual number for this contact">
													<IconButton
														sx={[
															{ backgroundColor: '#1559b3' },
															{ '&:hover': { backgroundColor: '#0c3e9a' } },
														]}
														onClick={() => setOpenManualNumberDialog(true)}
													>
														<AddIcCallOutlined fontSize="small" color="white" />
													</IconButton>
												</Tooltip>
											)}

											<Tooltip title={calledFields.length < 1 ? "Suppress the contact from being presented again" : "Contact has already been called"}>
												<IconButton
													sx={[
														{ backgroundColor: calledFields.length < 1 ? '#f44336' : '#9e9e9e' },
														{ '&:hover': { backgroundColor: calledFields.length < 1 ? '#d32f2f' : '#9e9e9e' } },
													]}
													onClick={() => {
														if (calledFields.length < 1) {
															handleContactAction(
																contactHash.current.phone,
																'suppress',
															);
														}
													}}
												>
													<DeleteForeverOutlined
														fontSize="small"
														color="white"
													/>
												</IconButton>
											</Tooltip>

											<Tooltip title="Dismiss Contact">
												<IconButton
													sx={[
														{ backgroundColor: '#1559b3' },
														{ '&:hover': { backgroundColor: '#0c3e9a' } },
													]}
													onClick={() => {
														if (calledFields.length < 1) {
															handleContactAction(
																contactHash.current.phone,
																'recycle',
															);
														} else {
															setActiveContact(null);
															setCalledFields([]);
															setCallingContactField('');
															setCallStatus('');
															contactHash.current = null;
														}
													}}
												>
													<CloseOutlined fontSize="small" color="white" />
												</IconButton>
											</Tooltip>
											{userContext.telephonyProvider === 'NiC' && (
												<Tooltip title="Warning: Due to how NICE Incontact handles dispositions, we can't guarantee Activity Log suppression is accurate.">
													<WarningAmberOutlined color="warning" />
												</Tooltip>)}
										</Box>
									)}
								</Box>
							</Box>
							<Box
								sx={{ backgroundColor: '#f2f6fa', borderRadius: '7px' }}
								display="flex"
								flexDirection="row"
								justifyContent="flex-end"
							>
								{activeContact && (
									<Tabs
										value={currentTab}
										onChange={(e, newValue) => setCurrentTab(newValue)}
										indicatorColor="primary"
										textColor="primary"
									>
										<Tab
											iconPosition="start"
											icon={<PersonOutline />}
											sx={tabStyle}
											label="Contact Info"
										></Tab>
										<Tab
											iconPosition="start"
											icon={<RestoreOutlined />}
											sx={tabStyle}
											label="Activity Log"
										></Tab>
									</Tabs>
								)}
							</Box>
						</Card>
					</Box>

					{/* Contact Info */}
					{activeContact && (
						<Box
							sx={{ width: '100%', padding: '10px' }}
							display="flex"
							flexDirection="column"
							alignItems="center"
						>
							<Card elevation={0} sx={{ width: '100%' }}>
								<Box>
									{currentTab === 0 && (
										<Box display="grid" gridTemplateColumns="1fr 2fr" gap={2}>
											<Card variant="outlined">
												<CardContent>
													<Typography variant="h5">Contact Info</Typography>
													<List>
														{activeContact?.source && (
															<ListItem>
																<Box display="flex" flexDirection="column">
																	<Typography variant="body1">
																		{activeContact.source
																			? activeContact.source
																			: 'No Source'}
																	</Typography>
																	<Typography variant="body2">
																		Source
																	</Typography>
																</Box>
															</ListItem>
														)}
														{activeContact?.outboundCallerId && (
															<ListItem>
																<Box display="flex" flexDirection="column">
																	<Typography variant="body1">
																		{activeContact.outboundCallerId
																			? activeContact.outboundCallerId
																			: 'No Outbound Caller ID'}
																	</Typography>
																	<Typography variant="body2">
																		Outbound Caller ID
																	</Typography>
																</Box>
															</ListItem>
														)}
														{activeContact?.email && (
															<ListItem>
																<Box display="flex" flexDirection="column">
																	<Typography variant="body1">
																		{activeContact.email
																			? activeContact.email
																			: 'No Email'}
																	</Typography>
																	<Typography variant="body2">Email</Typography>
																</Box>
															</ListItem>
														)}
														{activeContact?.address1 && (
															<ListItem>
																<Box display="flex" flexDirection="column">
																	<Typography variant="body1">
																		{activeContact.address1
																			? activeContact.address1
																			: 'No Address 1'}
																	</Typography>
																	<Typography variant="body2">
																		Address 1
																	</Typography>
																</Box>
															</ListItem>
														)}
														{activeContact?.address2 && (
															<ListItem>
																<Box display="flex" flexDirection="column">
																	<Typography variant="body1">
																		{activeContact.address2
																			? activeContact.address2
																			: 'No Address 2'}
																	</Typography>
																	<Typography variant="body2">
																		Address 2
																	</Typography>
																</Box>
															</ListItem>
														)}
														{activeContact?.city && (
															<ListItem>
																<Box display="flex" flexDirection="column">
																	<Typography variant="body1">
																		{activeContact.city
																			? activeContact.city
																			: 'No City'}
																	</Typography>
																	<Typography variant="body2">City</Typography>
																</Box>
															</ListItem>
														)}
														{activeContact?.state && (
															<ListItem>
																<Box display="flex" flexDirection="column">
																	<Typography variant="body1">
																		{activeContact.state
																			? activeContact.state
																			: 'No State'}
																	</Typography>
																	<Typography variant="body2">State</Typography>
																</Box>
															</ListItem>
														)}
														{activeContact?.zip && (
															<ListItem>
																<Box display="flex" flexDirection="column">
																	<Typography variant="body1">
																		{activeContact.zip
																			? activeContact.zip
																			: 'No Zip'}
																	</Typography>
																	<Typography variant="body2">Zip</Typography>
																</Box>
															</ListItem>
														)}
														{!activeContact.email &&
															!activeContact.address1 &&
															!activeContact.address2 &&
															!activeContact.city &&
															!activeContact.state &&
															!activeContact.zip &&
															!activeContact.source &&
															!activeContact.outboundCallerId &&
															(
																<Typography
																	sx={{ fontStyle: 'italic' }}
																	variant="body1"
																>
																	No Additional Contact Info
																</Typography>
															)}
													</List>
												</CardContent>
											</Card>
											<Card variant="outlined">
												<CardContent>
													<Typography variant="h5">Custom Fields</Typography>
													<Box
														display="grid"
														gridTemplateColumns="1fr 1fr 1fr"
														gap={2}
													>
														{userContext.customFields.map((field) => {
															if (activeContact.customFields?.[field.name] && field.type !== 'Phone') {
																return (
																	<ListItem key={field.name}>
																		<Box display="flex" flexDirection="column">
																			<Typography variant="body1">{activeContact.customFields?.[field.name]}</Typography>
																			<Typography variant="body2">{field.displayName}</Typography>
																		</Box>
																	</ListItem>
																);
															}
														})}
													</Box>
												</CardContent>
											</Card>
										</Box>
									)}
									{currentTab === 1 && (
										<Card variant="outlined">
											<Box sx={{ padding: '10px' }}>
												<ActivityLog contactId={activeContact.id} />
											</Box>
										</Card>
									)}
								</Box>
							</Card>
						</Box>
					)}
				</Box>
			</Box>
			<Dialog open={openManualNumberDialog} onClose={() => setOpenManualNumberDialog(false)}>
				<DialogTitle>Dial A Manual Number</DialogTitle>
				<DialogContent>
					<Box display="flex" gap={2} alignItems="center" sx={{ marginTop: '10px' }}>
						<TextField
							label="Phone Number"
							value={manualNumber}
							onChange={(e) => setManualNumber(phone(e.target.value).phoneNumber)}
							fullWidth
							variant="outlined"
							sx={{ marginBottom: '10px' }}
						/>
						<IconButton
							disabled={!phone(manualNumber).isValid}
							onClick={() => {
								if (manualNumber) {
									handleContactAction(manualNumber, 'approve');
									setManualNumber('');
									setOpenManualNumberDialog(false);
								}
							}}
							sx={[{ backgroundColor: '#1559b3' }, {
								'&:hover': {
									backgroundColor:
										'#0c3e9a'
								}
							}]}
						>
							<PhoneOutlined fontSize="small" color="white" />
						</IconButton>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button sx={actionTwoButtonStyle} onClick={() => {
						setOpenManualNumberDialog(false);
						setManualNumber('');
					}}>
						Cancel
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
